/**
 * 建單 wizard（20260611 反饋批次，SPEC §4）
 *
 * 五步驟：1 類型 → 2 行程 → 3 車款 → 4 加購/乘客 → 5 確認。
 * 四流程：接機（依航班/依時間＋多目的地）、送機（多上車點＋航班必填）、
 * 點對點（≥2 停點可排序）、包車（region＋時數選單）。
 * 計價：order.estimate 即時報價（港幣中心制，依需求方幣別顯示）；
 * 送出：order.create → auto-confirm 成功頁。
 * 入口：工作台 CTA `?mode=transfer|charter`、導航「建立用車需求」（無參數）。
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { skipToken } from "@tanstack/react-query";
import { Link } from "wouter";
import {
  ArrowLeft,
  ArrowRight,
  ArrowLeftRight,
  CalendarClock,
  Check,
  CheckCircle2,
  Info,
  ListPlus,
  PlaneLanding,
  PlaneTakeoff,
  Send,
} from "lucide-react";
import { trpc } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { Button } from "../../components/ui/button";
import { Input, Select } from "../../components/ui/input";
import { DateTimeField } from "../../components/order/DateTimeField";
import { isCompleteLocalDateTime } from "../../../../shared/local-datetime";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { fmtDateTime, fmtCurrency } from "../../lib/format";
import { AIRPORTS, REGIONS, SERVICE_TYPE_META, REGION_DIAL_CODES, DIAL_CODES, type Loc, type Region } from "../../../../shared/const";
import { creditStatus } from "../../../../shared/credit";
import { WhatsAppIcon, LineIcon, WechatIcon, KakaoIcon } from "../../components/icons/MessagingIcons";
import {
  BOOKING_AIRPORT_CODES,
  CREATABLE_SERVICE_TYPES,
  type BookingAirportCode,
  type CreatableServiceType,
} from "../../../../shared/booking";
import {
  REGION_LABEL,
  buildOrderPayload,
  deriveWizardRegion,
  emptyStop,
  fmtMoney,
  stopComplete,
  zoneLabel,
  type FormOptions,
  type WizardState,
} from "../../components/order/wizard/wizard-shared";
import { StopsEditor, PoiPickerDialog } from "../../components/order/wizard/StopsEditor";
import { VehicleModelPicker } from "../../components/order/wizard/VehicleModelPicker";
import { AddonPicker } from "../../components/order/wizard/AddonPicker";
import { PriceSummary } from "../../components/order/wizard/PriceSummary";

/* ── 常數與小工具 ───────────────────────────────────────────── */

const STEPS: Loc[] = [
  { "zh-TW": "類型", "en-US": "Type", "zh-CN": "类型" },
  { "zh-TW": "行程", "en-US": "Trip", "zh-CN": "行程" },
  { "zh-TW": "車款", "en-US": "Vehicle", "zh-CN": "车款" },
  { "zh-TW": "加購與乘客", "en-US": "Add-ons & passenger", "zh-CN": "加购与乘客" },
  { "zh-TW": "確認", "en-US": "Confirm", "zh-CN": "确认" },
];

const TYPE_ICON: Record<CreatableServiceType, typeof PlaneLanding> = {
  airport_pickup: PlaneLanding,
  airport_dropoff: PlaneTakeoff,
  point_to_point: ArrowLeftRight,
  charter: CalendarClock,
};

const TYPE_DESC: Record<CreatableServiceType, Loc> = {
  airport_pickup: { "zh-TW": "機場 → 飯店/市區，可多個目的地", "en-US": "Airport → hotel/city, multi-drop", "zh-CN": "机场 → 饭店/市区，可多个目的地" },
  airport_dropoff: { "zh-TW": "飯店/市區 → 機場，可多個上車點", "en-US": "Hotel/city → airport, multi-pickup", "zh-CN": "饭店/市区 → 机场，可多个上车点" },
  point_to_point: { "zh-TW": "市區點對點，可多個停點", "en-US": "City to city, multiple stops", "zh-CN": "市区点对点，可多个停点" },
  charter: { "zh-TW": "依時數包車，自由行程", "en-US": "Hourly charter, flexible itinerary", "zh-CN": "依时数包车，自由行程" },
};

const airportName = (code: BookingAirportCode, t: (d: Loc) => string): string => {
  const a = AIRPORTS.find((x) => x.code === code);
  return a ? `${t(a.name)}（${a.code}）` : code;
};

const initialState = (mode: string | null): WizardState => ({
  serviceType: mode === "charter" ? "charter" : null,
  airportCode: "",
  bookingMode: "by_flight",
  flightNumber: "",
  stops: [emptyStop()],
  charterRegion: "taiwan",
  charterPickupLocation: "",
  charterDropoffLocation: "",
  charterDurationHours: null,
  pickupTimeLocal: "",
  vehicleModelId: null,
  addonCodes: [],
  passengerName: "",
  passengerPhoneCc: "+852",
  passengerPhone: "",
  passengerCount: 1,
  luggageCount: 0,
  passengerWhatsapp: "",
  passengerLine: "",
  passengerWechat: "",
  passengerKakao: "",
  specialRequests: "",
});

type SuccessInfo = { id: number; orderNumber: string; displayCurrency: string; displayPrice: number };

/* ── 主元件 ─────────────────────────────────────────────────── */

export default function NewOrder() {
  const { t } = useLanguage();
  // mode 只在進頁時讀一次（工作台 CTA：transfer / charter）
  const [mode] = useState(() => new URLSearchParams(window.location.search).get("mode"));
  const [step, setStep] = useState(mode === "charter" ? 2 : 1);
  const [s, setS] = useState<WizardState>(() => initialState(mode));
  const [triedNext, setTriedNext] = useState(false);
  const [success, setSuccess] = useState<SuccessInfo | null>(null);

  const optionsQ = trpc.bookingData.formOptions.useQuery();
  const opts = optionsQ.data;

  const set = <K extends keyof WizardState>(k: K, v: WizardState[K]) => setS((prev) => ({ ...prev, [k]: v }));

  // D7 A6：乘客電話國碼預設依需求方地區（opts 載入後設一次，之後由使用者自行調整）
  const ccInit = useRef(false);
  useEffect(() => {
    if (opts && !ccInit.current) {
      ccInit.current = true;
      set("passengerPhoneCc", REGION_DIAL_CODES[opts.agencyRegion]);
    }
  }, [opts]);

  /** 切換服務類型：清掉不相干欄位，避免殘留污染 payload */
  const selectType = (st: CreatableServiceType) =>
    setS((prev) => ({
      ...initialState(null),
      serviceType: st,
      stops: st === "point_to_point" ? [emptyStop(), emptyStop()] : [emptyStop()],
      // 乘客資料屬共通欄位，切流程保留減少重填
      passengerName: prev.passengerName,
      passengerPhoneCc: prev.passengerPhoneCc,
      passengerPhone: prev.passengerPhone,
      passengerCount: prev.passengerCount,
      luggageCount: prev.luggageCount,
      passengerWhatsapp: prev.passengerWhatsapp,
      passengerLine: prev.passengerLine,
      passengerWechat: prev.passengerWechat,
      passengerKakao: prev.passengerKakao,
      specialRequests: prev.specialRequests,
    }));

  const region = deriveWizardRegion(s);

  // 包車時數選單：該 region 的 active 方案時數（去重排序），附顯示幣別價格範圍
  const charterDurations = useMemo(() => {
    if (!opts) return [];
    const plans = opts.charterPlans.filter((p) => p.region === s.charterRegion);
    const hours = [...new Set(plans.map((p) => p.durationHours))].sort((a, b) => a - b);
    return hours.map((h) => {
      const prices = plans.filter((p) => p.durationHours === h).map((p) => Number(p.priceHkd));
      const rate = opts.exchangeRate;
      const [lo, hi] = [Math.min(...prices), Math.max(...prices)];
      const conv = (x: number) => (rate === null ? x : Math.round(x * rate * 100) / 100);
      return { hours: h, lo: conv(lo), hi: conv(hi), currency: rate === null ? "HKD" : opts.displayCurrency };
    });
  }, [opts, s.charterRegion]);

  // 車款選項：region 相符或共用；包車另需該 region×時數有方案
  const availableModels = useMemo(() => {
    if (!opts) return [];
    return opts.vehicleModels.filter((m) => {
      if (m.region !== null && region !== null && m.region !== region) return false;
      if (s.serviceType === "charter") {
        if (s.charterDurationHours === null) return false;
        return opts.charterPlans.some(
          (p) => p.region === s.charterRegion && p.vehicleModelId === m.id && p.durationHours === s.charterDurationHours,
        );
      }
      return true;
    });
  }, [opts, region, s.serviceType, s.charterRegion, s.charterDurationHours]);

  const selectedModel = availableModels.find((m) => m.id === s.vehicleModelId) ?? null;
  const paxOver = selectedModel !== null && s.passengerCount > selectedModel.maxPassengers;
  const luggageOver = selectedModel !== null && s.luggageCount > selectedModel.maxLuggage;

  // 出發時間防呆（client；server 亦驗）
  const pickupMs = s.pickupTimeLocal === "" ? null : new Date(s.pickupTimeLocal).getTime();
  const pickupInPast = pickupMs !== null && Number.isFinite(pickupMs) && pickupMs <= Date.now();

  /* ── 各步驟完成判定 ── */
  const stopsOk = (min: number) => s.stops.length >= min && s.stops.every(stopComplete);
  const timeOk = pickupMs !== null && Number.isFinite(pickupMs) && !pickupInPast;
  const tripValid = (() => {
    switch (s.serviceType) {
      case "airport_pickup":
        return (
          s.airportCode !== "" &&
          stopsOk(1) &&
          (s.bookingMode !== "by_flight" || s.flightNumber.trim() !== "") &&
          timeOk
        );
      case "airport_dropoff":
        return stopsOk(1) && s.airportCode !== "" && s.flightNumber.trim() !== "" && timeOk;
      case "point_to_point":
        return stopsOk(2) && timeOk;
      case "charter":
        return (
          s.charterPickupLocation.trim() !== "" &&
          s.charterDropoffLocation.trim() !== "" &&
          s.charterDurationHours !== null &&
          timeOk
        );
      default:
        return false;
    }
  })();
  const vehicleValid = selectedModel !== null;
  // D7 A9：電話＋四通訊整組至少填一個
  const anyContact =
    s.passengerPhone.trim() !== "" ||
    s.passengerWhatsapp.trim() !== "" ||
    s.passengerLine.trim() !== "" ||
    s.passengerWechat.trim() !== "" ||
    s.passengerKakao.trim() !== "";
  const paxValid =
    s.passengerName.trim() !== "" && s.passengerCount >= 1 && !paxOver && !luggageOver && anyContact;

  const stepValid = [s.serviceType !== null, tripValid, vehicleValid, paxValid, true][step - 1] ?? false;

  const payload = useMemo(
    () => (opts ? buildOrderPayload(s, opts.charterPlans) : null),
    [s, opts],
  );

  const estimateQ = trpc.order.estimate.useQuery(step === 5 && payload !== null ? payload : skipToken);
  // D2 信用額度：本單 priceHkd 加上已用額度是否超過額度（未設限則永不超）
  const creditQ = trpc.order.myCredit.useQuery(undefined, { staleTime: 30_000 });
  const overCredit = (() => {
    const c = creditQ.data;
    const priceHkd = estimateQ.data?.priceHkd ?? null;
    if (!c || c.limit == null || priceHkd == null) return false;
    return creditStatus(c.limit, c.used, priceHkd).wouldExceed;
  })();

  const createM = trpc.order.create.useMutation({
    onSuccess: (res) => setSuccess(res),
  });

  const goNext = () => {
    if (!stepValid) {
      setTriedNext(true);
      return;
    }
    setTriedNext(false);
    setStep((x) => Math.min(5, x + 1));
  };
  const goBack = () => {
    setTriedNext(false);
    setStep((x) => Math.max(1, x - 1));
  };

  /* ── 成功頁 ── */
  if (success) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <Card className="overflow-hidden border-[color-mix(in_oklab,var(--color-success)_35%,transparent)]">
          <div className="bg-[color-mix(in_oklab,var(--color-success)_10%,transparent)] p-6 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--color-success)]" strokeWidth={1.5} />
            <h1 className="mt-3 font-display text-2xl font-medium tracking-tight">
              {t({ "zh-TW": "訂單已成立（自動確認）", "en-US": "Order placed (auto-confirmed)", "zh-CN": "订单已成立（自动确认）" })}
            </h1>
            <p className="mt-1 text-[13px] text-muted-foreground">
              {t({
                "zh-TW": "已進入平台派遣流程，派遣與司機資訊會即時通知你。",
                "en-US": "Dispatching is underway — you'll be notified of fleet and driver details.",
                "zh-CN": "已进入平台派遣流程，派遣与司机资讯会即时通知你。",
              })}
            </p>
          </div>
          <CardContent className="space-y-3 p-6">
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted-foreground">{t({ "zh-TW": "訂單編號", "en-US": "Order number", "zh-CN": "订单编号" })}</span>
              <span className="font-mono font-medium">{success.orderNumber}</span>
            </div>
            <div className="flex items-center justify-between text-[13px]">
              <span className="text-muted-foreground">{t({ "zh-TW": "金額", "en-US": "Amount", "zh-CN": "金额" })}</span>
              <span className="numeric font-medium">{fmtMoney(success.displayCurrency, success.displayPrice)}</span>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setSuccess(null);
                  setS(initialState(mode));
                  setStep(mode === "charter" ? 2 : 1);
                  createM.reset();
                }}
              >
                {t({ "zh-TW": "再建一單", "en-US": "New order", "zh-CN": "再建一单" })}
              </Button>
              <Link href={`/orders/${success.id}`}>
                <Button>
                  {t({ "zh-TW": "查看訂單", "en-US": "View order", "zh-CN": "查看订单" })}
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  /* ── wizard 主畫面 ── */
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <p className="font-mono text-[12px] uppercase tracking-[0.18em] text-primary">
          {t({ "zh-TW": "新增訂單", "en-US": "New request", "zh-CN": "新增订单" })}
        </p>
        <h1 className="font-display text-3xl font-medium tracking-tight">
          {t({ "zh-TW": "建立用車需求", "en-US": "Create vehicle request", "zh-CN": "建立用车需求" })}
        </h1>
        <p className="mt-2 text-[13px] text-muted-foreground">
          {t({
            "zh-TW": "完成五個步驟即時取得報價，送出後自動確認、直接進入派遣。",
            "en-US": "Five quick steps with instant pricing — orders auto-confirm into dispatch.",
            "zh-CN": "完成五个步骤即时取得报价，送出后自动确认、直接进入派遣。",
          })}
        </p>
      </div>

      {/* 步驟條 */}
      <ol className="flex items-center gap-1.5" aria-label={t({ "zh-TW": "建單步驟", "en-US": "Steps", "zh-CN": "建单步骤" })}>
        {STEPS.map((label, i) => {
          const n = i + 1;
          const state = n < step ? "done" : n === step ? "current" : "todo";
          return (
            <li key={n} className="flex min-w-0 flex-1 items-center gap-1.5">
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${
                  state === "done"
                    ? "bg-primary text-primary-foreground"
                    : state === "current"
                      ? "border-2 border-primary bg-primary-soft text-primary"
                      : "border border-border bg-muted text-muted-foreground"
                }`}
              >
                {state === "done" ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : n}
              </span>
              <span
                className={`hidden truncate text-[12px] sm:block ${
                  state === "current" ? "font-medium text-foreground" : "text-muted-foreground"
                }`}
              >
                {t(label)}
              </span>
              {n < STEPS.length && <span className="h-px min-w-2 flex-1 bg-border" />}
            </li>
          );
        })}
      </ol>

      <Card>
        <CardContent className="p-6">
          {!opts ? (
            <div className="h-48 animate-pulse rounded-lg bg-muted/40" />
          ) : (
            <div className="space-y-6">
              {/* ── 步驟 1：類型 ── */}
              {step === 1 && (
                <section className="space-y-3">
                  <h2 className="font-display text-lg font-medium tracking-tight">
                    {t({ "zh-TW": "選擇服務類型", "en-US": "Choose a service", "zh-CN": "选择服务类型" })}
                  </h2>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {CREATABLE_SERVICE_TYPES.filter((st) => (mode === "transfer" ? st !== "charter" : true)).map(
                      (st) => {
                        const Icon = TYPE_ICON[st];
                        const active = s.serviceType === st;
                        return (
                          <button
                            key={st}
                            type="button"
                            aria-pressed={active}
                            onClick={() => {
                              if (s.serviceType !== st) selectType(st);
                            }}
                            className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-all hover:-translate-y-px hover:shadow-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                              active ? "border-primary bg-primary-soft/40" : "border-border bg-card hover:border-border-strong"
                            }`}
                          >
                            <span
                              className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md border ${
                                active ? "border-primary/40 bg-primary text-primary-foreground" : "border-border bg-muted/50 text-foreground/70"
                              }`}
                            >
                              <Icon className="h-4 w-4" strokeWidth={1.75} />
                            </span>
                            <span className="min-w-0">
                              <span className="block font-display text-[15px] font-medium leading-tight">
                                {t(SERVICE_TYPE_META[st].label)}
                              </span>
                              <span className="mt-0.5 block text-[12px] text-muted-foreground">{t(TYPE_DESC[st])}</span>
                            </span>
                          </button>
                        );
                      },
                    )}
                  </div>
                  {triedNext && s.serviceType === null && (
                    <p className="text-[12px] text-destructive">
                      {t({ "zh-TW": "請先選擇服務類型。", "en-US": "Please choose a service type.", "zh-CN": "请先选择服务类型。" })}
                    </p>
                  )}
                </section>
              )}

              {/* ── 步驟 2：行程 ── */}
              {step === 2 && s.serviceType !== null && (
                <TripStep
                  s={s}
                  set={set}
                  opts={opts}
                  region={region}
                  charterDurations={charterDurations}
                  triedNext={triedNext}
                  pickupInPast={pickupInPast}
                />
              )}

              {/* ── 步驟 3：車款 ── */}
              {step === 3 && (
                <section className="space-y-3">
                  <div>
                    <h2 className="font-display text-lg font-medium tracking-tight">
                      {t({ "zh-TW": "選擇車款", "en-US": "Choose a vehicle", "zh-CN": "选择车款" })}
                    </h2>
                    <p className="text-[12px] text-muted-foreground">
                      {region !== null &&
                        `${t({ "zh-TW": "服務區域", "en-US": "Service region", "zh-CN": "服务区域" })}：${t(REGION_LABEL[region])}`}
                    </p>
                    <p className="mt-1 text-[12px] text-muted-foreground">
                      {t({
                        "zh-TW": "每張訂單限一台車；人數／行李超過所選車款上限將無法送出。如需多台請分開建單。",
                        "en-US": "One vehicle per order; orders over the vehicle's capacity can't be submitted. Book separately for more vehicles.",
                        "zh-CN": "每张订单限一台车；人数／行李超过所选车款上限将无法送出。如需多台请分开建单。",
                      })}
                    </p>
                  </div>
                  <VehicleModelPicker
                    models={availableModels}
                    selectedId={s.vehicleModelId}
                    onSelect={(id) => set("vehicleModelId", id)}
                  />
                  {triedNext && !vehicleValid && (
                    <p className="text-[12px] text-destructive">
                      {t({ "zh-TW": "請選擇車款。", "en-US": "Please choose a vehicle model.", "zh-CN": "请选择车款。" })}
                    </p>
                  )}
                </section>
              )}

              {/* ── 步驟 4：加購／乘客 ── */}
              {step === 4 && (
                <section className="space-y-6">
                  <div className="space-y-3">
                    <h2 className="font-display text-lg font-medium tracking-tight">
                      {t({ "zh-TW": "加購服務", "en-US": "Add-ons", "zh-CN": "加购服务" })}
                    </h2>
                    <AddonPicker
                      addons={opts.addonServices}
                      selected={s.addonCodes}
                      onChange={(codes) => set("addonCodes", codes)}
                      displayCurrency={opts.displayCurrency}
                    />
                  </div>

                  <div className="space-y-3">
                    <h2 className="font-display text-lg font-medium tracking-tight">
                      {t({ "zh-TW": "乘客資料", "en-US": "Passenger details", "zh-CN": "乘客资料" })}
                    </h2>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label className="text-[12px]">
                          {t({ "zh-TW": "乘客姓名", "en-US": "Passenger name", "zh-CN": "乘客姓名" })} *
                        </Label>
                        <Input value={s.passengerName} onChange={(e) => set("passengerName", e.target.value)} />
                        {triedNext && s.passengerName.trim() === "" && (
                          <p className="text-[12px] text-destructive">
                            {t({ "zh-TW": "請填寫乘客姓名。", "en-US": "Passenger name is required.", "zh-CN": "请填写乘客姓名。" })}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[12px]">{t({ "zh-TW": "乘客電話", "en-US": "Passenger phone", "zh-CN": "乘客电话" })}</Label>
                        <div className="flex gap-2">
                          <Select
                            className="w-24 shrink-0"
                            value={s.passengerPhoneCc}
                            onChange={(e) => set("passengerPhoneCc", e.target.value)}
                            aria-label={t({ "zh-TW": "國碼", "en-US": "Country code", "zh-CN": "国码" })}
                          >
                            {DIAL_CODES.map((c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ))}
                          </Select>
                          <Input
                            inputMode="tel"
                            value={s.passengerPhone}
                            onChange={(e) => set("passengerPhone", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-[12px]">{t({ "zh-TW": "人數", "en-US": "Passengers", "zh-CN": "人数" })}</Label>
                        <Input
                          type="number"
                          min={1}
                          max={99}
                          value={s.passengerCount}
                          onChange={(e) =>
                            set("passengerCount", Math.max(1, Math.min(99, Math.trunc(Number(e.target.value) || 1))))
                          }
                        />
                        {paxOver && selectedModel && (
                          <p className="text-[12px] text-destructive">
                            {t({
                              "zh-TW": `超過「${selectedModel.name}」上限 ${selectedModel.maxPassengers} 人，請回上一步改選車款。`,
                              "en-US": `Exceeds ${selectedModel.name} limit of ${selectedModel.maxPassengers} pax — go back to pick a larger vehicle.`,
                              "zh-CN": `超过「${selectedModel.name}」上限 ${selectedModel.maxPassengers} 人，请回上一步改选车款。`,
                            })}
                          </p>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-[12px]">{t({ "zh-TW": "行李件數", "en-US": "Luggage", "zh-CN": "行李件数" })}</Label>
                        <Input
                          type="number"
                          min={0}
                          max={99}
                          value={s.luggageCount}
                          onChange={(e) =>
                            set("luggageCount", Math.max(0, Math.min(99, Math.trunc(Number(e.target.value) || 0))))
                          }
                        />
                        {luggageOver && selectedModel && (
                          <p className="text-[12px] text-destructive">
                            {t({
                              "zh-TW": `超過「${selectedModel.name}」行李上限 ${selectedModel.maxLuggage} 件。`,
                              "en-US": `Exceeds ${selectedModel.name} luggage limit of ${selectedModel.maxLuggage}.`,
                              "zh-CN": `超过「${selectedModel.name}」行李上限 ${selectedModel.maxLuggage} 件。`,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-[12px]">
                        {t({ "zh-TW": "即時通訊（電話與以下至少填一項）", "en-US": "Messaging (phone or one of below)", "zh-CN": "即时通讯（电话与以下至少填一项）" })}
                      </Label>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <ContactInput icon={<WhatsAppIcon />} label="WhatsApp" value={s.passengerWhatsapp} onChange={(v) => set("passengerWhatsapp", v)} />
                        <ContactInput icon={<LineIcon />} label="LINE" value={s.passengerLine} onChange={(v) => set("passengerLine", v)} />
                        <ContactInput icon={<WechatIcon />} label="WeChat" value={s.passengerWechat} onChange={(v) => set("passengerWechat", v)} />
                        <ContactInput icon={<KakaoIcon />} label="Kakao Talk" value={s.passengerKakao} onChange={(v) => set("passengerKakao", v)} />
                      </div>
                      {triedNext && !anyContact && (
                        <p className="text-[12px] text-destructive">
                          {t({ "zh-TW": "請至少填寫一種聯絡方式（電話或即時通訊）。", "en-US": "Please provide at least one contact (phone or messaging).", "zh-CN": "请至少填写一种联络方式（电话或即时通讯）。" })}
                        </p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[12px]">{t({ "zh-TW": "特殊需求 / 備註", "en-US": "Special requests", "zh-CN": "特殊需求 / 备注" })}</Label>
                      <Textarea
                        value={s.specialRequests}
                        onChange={(e) => set("specialRequests", e.target.value)}
                        placeholder={t({ "zh-TW": "無菸車、語言需求…", "en-US": "Non-smoking, language…", "zh-CN": "无烟车、语言需求…" })}
                      />
                    </div>
                  </div>
                </section>
              )}

              {/* ── 步驟 5：確認 ── */}
              {step === 5 && s.serviceType !== null && (
                <section className="space-y-5">
                  <h2 className="font-display text-lg font-medium tracking-tight">
                    {t({ "zh-TW": "確認訂單", "en-US": "Review & confirm", "zh-CN": "确认订单" })}
                  </h2>

                  <div className="space-y-2.5 rounded-md border border-border bg-muted/30 p-4 text-[13px]">
                    <SummaryRow label={{ "zh-TW": "服務類型", "en-US": "Service", "zh-CN": "服务类型" }}>
                      {t(SERVICE_TYPE_META[s.serviceType].label)}
                    </SummaryRow>
                    <SummaryRow label={{ "zh-TW": "行程", "en-US": "Trip", "zh-CN": "行程" }}>
                      <TripSummary s={s} />
                    </SummaryRow>
                    {(s.serviceType === "airport_pickup" || s.serviceType === "airport_dropoff") && (
                      <SummaryRow label={{ "zh-TW": "預約方式", "en-US": "Booking mode", "zh-CN": "预约方式" }}>
                        {s.serviceType === "airport_pickup" && s.bookingMode === "by_flight"
                          ? `${t({ "zh-TW": "依航班", "en-US": "By flight", "zh-CN": "依航班" })} · ${s.flightNumber.trim()}`
                          : s.serviceType === "airport_dropoff"
                            ? `${t({ "zh-TW": "指定時間", "en-US": "By time", "zh-CN": "指定时间" })} · ${t({ "zh-TW": "航班", "en-US": "Flight", "zh-CN": "航班" })} ${s.flightNumber.trim()}`
                            : t({ "zh-TW": "依時間", "en-US": "By time", "zh-CN": "依时间" })}
                      </SummaryRow>
                    )}
                    <SummaryRow
                      label={
                        s.serviceType === "airport_pickup" && s.bookingMode === "by_flight"
                          ? { "zh-TW": "航班抵達時間", "en-US": "Flight arrival", "zh-CN": "航班抵达时间" }
                          : { "zh-TW": "出發時間", "en-US": "Pickup time", "zh-CN": "出发时间" }
                      }
                    >
                      <span className="font-semibold">{pickupMs !== null ? fmtDateTime(pickupMs) : "—"}</span>
                    </SummaryRow>
                    <SummaryRow label={{ "zh-TW": "車款", "en-US": "Vehicle", "zh-CN": "车款" }}>
                      {selectedModel?.name ?? "—"}
                    </SummaryRow>
                    {/* 人數獨立一列並加粗：先前擠在車款那行，人數被誤改也不容易在確認頁發現 */}
                    <SummaryRow label={{ "zh-TW": "人數 / 行李", "en-US": "Passengers / bags", "zh-CN": "人数 / 行李" }}>
                      <span className="font-semibold">
                        {s.passengerCount} {t({ "zh-TW": "人", "en-US": "pax", "zh-CN": "人" })}
                      </span>
                      {" · "}
                      {s.luggageCount} {t({ "zh-TW": "件行李", "en-US": "bags", "zh-CN": "件行李" })}
                    </SummaryRow>
                    <SummaryRow label={{ "zh-TW": "乘客", "en-US": "Passenger", "zh-CN": "乘客" }}>
                      {s.passengerName.trim()}
                      {s.passengerPhone.trim() !== "" && ` · ${s.passengerPhoneCc} ${s.passengerPhone.trim()}`}
                      {s.passengerWhatsapp.trim() !== "" && ` · WhatsApp ${s.passengerWhatsapp.trim()}`}
                      {s.passengerLine.trim() !== "" && ` · LINE ${s.passengerLine.trim()}`}
                      {s.passengerWechat.trim() !== "" && ` · WeChat ${s.passengerWechat.trim()}`}
                      {s.passengerKakao.trim() !== "" && ` · Kakao ${s.passengerKakao.trim()}`}
                    </SummaryRow>
                    {s.addonCodes.length > 0 && (
                      <SummaryRow label={{ "zh-TW": "加購", "en-US": "Add-ons", "zh-CN": "加购" }}>
                        {opts.addonServices
                          .filter((a) => (s.addonCodes as string[]).includes(a.code))
                          .map((a) => a.name)
                          .join("、")}
                      </SummaryRow>
                    )}
                    {s.specialRequests.trim() !== "" && (
                      <SummaryRow label={{ "zh-TW": "備註", "en-US": "Notes", "zh-CN": "备注" }}>
                        {s.specialRequests.trim()}
                      </SummaryRow>
                    )}
                  </div>

                  <PriceSummary estimate={estimateQ.data} isLoading={estimateQ.isLoading} />

                  {overCredit && (
                    <div className="rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_30%,transparent)] bg-destructive-soft px-3 py-2 text-[12px] text-destructive">
                      {t({
                        "zh-TW": `此訂單將超過信用額度（可用 ${fmtCurrency(creditQ.data?.available ?? 0, "HKD")}），請聯繫平台調度調整額度後再送出。`,
                        "en-US": `This order exceeds your credit limit (available ${fmtCurrency(creditQ.data?.available ?? 0, "HKD")}). Please contact the platform to adjust it.`,
                        "zh-CN": `此订单将超过信用额度（可用 ${fmtCurrency(creditQ.data?.available ?? 0, "HKD")}），请联系平台调度调整额度后再送出。`,
                      })}
                    </div>
                  )}

                  {estimateQ.error && (
                    <div className="rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_30%,transparent)] bg-destructive-soft px-3 py-2 text-[12px] text-destructive">
                      {estimateQ.error.message}
                    </div>
                  )}

                  {createM.error && (
                    <div className="rounded-md border border-[color-mix(in_oklab,var(--color-destructive)_30%,transparent)] bg-destructive-soft px-3 py-2 text-[12px] text-destructive">
                      {createM.error.message}
                    </div>
                  )}
                </section>
              )}

              {/* ── 導航列 ── */}
              <div className="flex items-center justify-between border-t border-border pt-4">
                <Button type="button" variant="ghost" onClick={goBack} disabled={step === 1}>
                  <ArrowLeft className="h-4 w-4" />
                  {t({ "zh-TW": "上一步", "en-US": "Back", "zh-CN": "上一步" })}
                </Button>
                {step < 5 ? (
                  <Button type="button" onClick={goNext}>
                    {t({ "zh-TW": "下一步", "en-US": "Next", "zh-CN": "下一步" })}
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    size="lg"
                    disabled={
                      payload === null ||
                      estimateQ.isLoading ||
                      estimateQ.data?.priceHkd == null ||
                      overCredit ||
                      createM.isPending
                    }
                    onClick={() => payload !== null && createM.mutate(payload)}
                  >
                    <Send className="h-4 w-4" />
                    {createM.isPending
                      ? t({ "zh-TW": "送出中…", "en-US": "Submitting…", "zh-CN": "送出中…" })
                      : t({ "zh-TW": "送出訂單", "en-US": "Place order", "zh-CN": "送出订单" })}
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

/* ── 步驟 2：行程（依流程切換） ─────────────────────────────── */

function TripStep({
  s,
  set,
  opts,
  region,
  charterDurations,
  triedNext,
  pickupInPast,
}: {
  s: WizardState;
  set: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
  opts: FormOptions;
  region: Region | null;
  charterDurations: { hours: number; lo: number; hi: number; currency: string }[];
  triedNext: boolean;
  pickupInPast: boolean;
}) {
  const { t } = useLanguage();
  const [charterPickerFor, setCharterPickerFor] = useState<"pickup" | "dropoff" | null>(null);
  const st = s.serviceType;
  if (st === null) return null;

  const airportSelect = (
    <div className="space-y-1.5">
      <Label className="text-[12px]">
        {st === "airport_pickup"
          ? t({ "zh-TW": "出發機場", "en-US": "Arrival airport", "zh-CN": "出发机场" })
          : t({ "zh-TW": "目的地機場", "en-US": "Departure airport", "zh-CN": "目的地机场" })}{" "}
        *
      </Label>
      <Select
        value={s.airportCode}
        onChange={(e) => set("airportCode", e.target.value as WizardState["airportCode"])}
      >
        <option value="">{t({ "zh-TW": "選擇機場", "en-US": "Choose airport", "zh-CN": "选择机场" })}</option>
        {BOOKING_AIRPORT_CODES.map((code) => (
          <option key={code} value={code}>
            {airportName(code, t)}
          </option>
        ))}
      </Select>
      {triedNext && s.airportCode === "" && (
        <p className="text-[12px] text-destructive">
          {t({ "zh-TW": "請選擇機場。", "en-US": "Please choose an airport.", "zh-CN": "请选择机场。" })}
        </p>
      )}
    </div>
  );

  const isFlightEta = st === "airport_pickup" && s.bookingMode === "by_flight";
  const timeField = (
    <DateTimeField
      idPrefix="pickup"
      label={
        isFlightEta
          ? { "zh-TW": "航班預計抵達時間", "en-US": "Flight arrival time (ETA)", "zh-CN": "航班预计抵达时间" }
          : { "zh-TW": "出發日期時間", "en-US": "Pickup date & time", "zh-CN": "出发日期时间" }
      }
      hint={
        isFlightEta
          ? {
              "zh-TW": "請填航班表定抵達時間，不是您希望上車的時間。司機依此時間到場，含 1 小時等候。",
              "en-US": "Enter the flight's scheduled arrival time, not when you'd like to be picked up. The driver arrives accordingly, with 1 hour of waiting included.",
              "zh-CN": "请填航班表定抵达时间，不是您希望上车的时间。司机依此时间到场，含 1 小时等候。",
            }
          : undefined
      }
      value={s.pickupTimeLocal}
      onChange={(v) => set("pickupTimeLocal", v)}
      error={
        pickupInPast
          ? t({ "zh-TW": "不可選擇過去的時間。", "en-US": "Time cannot be in the past.", "zh-CN": "不可选择过去的时间。" })
          : // 只填一半（例如只選了日期）也要提示，不能靜靜擋著讓使用者不知道卡在哪
            triedNext && !isCompleteLocalDateTime(s.pickupTimeLocal)
            ? t({ "zh-TW": "請完整選擇日期與時間。", "en-US": "Please choose both date and time.", "zh-CN": "请完整选择日期与时间。" })
            : null
      }
    />
  );

  const stopsIncomplete = triedNext && !s.stops.every(stopComplete);

  /* 接機 */
  if (st === "airport_pickup") {
    return (
      <section className="space-y-5">
        <h2 className="font-display text-lg font-medium tracking-tight">
          {t({ "zh-TW": "接機行程", "en-US": "Pickup trip", "zh-CN": "接机行程" })}
        </h2>
        {airportSelect}

        <div className="space-y-1.5">
          <Label className="text-[12px]">{t({ "zh-TW": "目的地", "en-US": "Destinations", "zh-CN": "目的地" })} *</Label>
          <StopsEditor
            stops={s.stops}
            onChange={(stops) => set("stops", stops)}
            pois={opts.pois}
            region={region}
            minStops={1}
            itemLabel={{ "zh-TW": "目的地", "en-US": "Destination", "zh-CN": "目的地" }}
            addLabel={{ "zh-TW": "新增目的地", "en-US": "Add destination", "zh-CN": "新增目的地" }}
          />
          {stopsIncomplete && (
            <p className="text-[12px] text-destructive">
              {t({ "zh-TW": "每個目的地需從清單選擇，或自行輸入並補選區域。", "en-US": "Each destination needs a list pick, or typed location with a zone.", "zh-CN": "每个目的地需从清单选择，或自行输入并补选区域。" })}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label className="text-[12px]">{t({ "zh-TW": "預約方式", "en-US": "Booking mode", "zh-CN": "预约方式" })}</Label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {(["by_flight", "by_time"] as const).map((m) => {
              const active = s.bookingMode === m;
              return (
                <label
                  key={m}
                  className={`flex cursor-pointer items-center gap-2.5 rounded-md border px-3.5 py-2.5 transition-colors ${
                    active ? "border-primary bg-primary-soft/40" : "border-border bg-card hover:border-border-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="bookingMode"
                    className="accent-[var(--color-primary)]"
                    checked={active}
                    onChange={() => set("bookingMode", m)}
                  />
                  <span className="text-[13px] font-medium">
                    {m === "by_flight"
                      ? t({ "zh-TW": "依航班", "en-US": "By flight", "zh-CN": "依航班" })
                      : t({ "zh-TW": "依時間", "en-US": "By time", "zh-CN": "依时间" })}
                  </span>
                </label>
              );
            })}
          </div>
          {s.bookingMode === "by_flight" && (
            <div className="space-y-1.5">
              <Input
                value={s.flightNumber}
                onChange={(e) => set("flightNumber", e.target.value)}
                placeholder={t({ "zh-TW": "航班編號，例：CX450", "en-US": "Flight number, e.g. CX450", "zh-CN": "航班编号，例：CX450" })}
              />
              {triedNext && s.flightNumber.trim() === "" && (
                <p className="text-[12px] text-destructive">
                  {t({ "zh-TW": "依航班接機需填寫航班編號。", "en-US": "Flight number is required for by-flight pickup.", "zh-CN": "依航班接机需填写航班编号。" })}
                </p>
              )}
              <p className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {t({
                  "zh-TW": "依航班接機含 1 小時等候；超時費用另計（線下計收）。",
                  "en-US": "By-flight pickup includes 1 hour of waiting; overtime is billed offline.",
                  "zh-CN": "依航班接机含 1 小时等候；超时费用另计（线下计收）。",
                })}
              </p>
            </div>
          )}
        </div>

        {timeField}
      </section>
    );
  }

  /* 送機 */
  if (st === "airport_dropoff") {
    return (
      <section className="space-y-5">
        <h2 className="font-display text-lg font-medium tracking-tight">
          {t({ "zh-TW": "送機行程", "en-US": "Drop-off trip", "zh-CN": "送机行程" })}
        </h2>

        <div className="space-y-1.5">
          <Label className="text-[12px]">{t({ "zh-TW": "上車地點", "en-US": "Pickup points", "zh-CN": "上车地点" })} *</Label>
          <StopsEditor
            stops={s.stops}
            onChange={(stops) => set("stops", stops)}
            pois={opts.pois}
            region={region}
            minStops={1}
            itemLabel={{ "zh-TW": "上車點", "en-US": "Pickup point", "zh-CN": "上车点" }}
            addLabel={{ "zh-TW": "新增上車點", "en-US": "Add pickup point", "zh-CN": "新增上车点" }}
          />
          {stopsIncomplete && (
            <p className="text-[12px] text-destructive">
              {t({ "zh-TW": "每個上車點需從清單選擇，或自行輸入並補選區域。", "en-US": "Each pickup point needs a list pick, or typed location with a zone.", "zh-CN": "每个上车点需从清单选择，或自行输入并补选区域。" })}
            </p>
          )}
        </div>

        {airportSelect}

        <div className="space-y-1.5">
          <Label className="text-[12px]">{t({ "zh-TW": "航班編號", "en-US": "Flight number", "zh-CN": "航班编号" })} *</Label>
          <Input
            value={s.flightNumber}
            onChange={(e) => set("flightNumber", e.target.value)}
            placeholder="BR0857"
          />
          {triedNext && s.flightNumber.trim() === "" && (
            <p className="text-[12px] text-destructive">
              {t({ "zh-TW": "送機需填寫航班編號。", "en-US": "Flight number is required for drop-off.", "zh-CN": "送机需填写航班编号。" })}
            </p>
          )}
          <p className="flex items-start gap-1.5 text-[12px] text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            {t({
              "zh-TW": "業者將依航班判斷航廈；送機僅依你指定的時間出發。",
              "en-US": "The operator uses the flight to determine the terminal; departure follows your specified time.",
              "zh-CN": "业者将依航班判断航厦；送机仅依你指定的时间出发。",
            })}
          </p>
        </div>

        {timeField}
      </section>
    );
  }

  /* 點對點 */
  if (st === "point_to_point") {
    return (
      <section className="space-y-5">
        <h2 className="font-display text-lg font-medium tracking-tight">
          {t({ "zh-TW": "點對點行程", "en-US": "Point-to-point trip", "zh-CN": "点对点行程" })}
        </h2>
        <div className="space-y-1.5">
          <Label className="text-[12px]">
            {t({ "zh-TW": "停點（依順序行駛，至少 2 點）", "en-US": "Stops (in order, min 2)", "zh-CN": "停点（依顺序行驶，至少 2 点）" })} *
          </Label>
          <StopsEditor
            stops={s.stops}
            onChange={(stops) => set("stops", stops)}
            pois={opts.pois}
            region={region}
            minStops={2}
            itemLabel={{ "zh-TW": "停點", "en-US": "Stop", "zh-CN": "停点" }}
            addLabel={{ "zh-TW": "新增停點", "en-US": "Add stop", "zh-CN": "新增停点" }}
          />
          {stopsIncomplete && (
            <p className="text-[12px] text-destructive">
              {t({ "zh-TW": "每個停點需從清單選擇，或自行輸入並補選區域。", "en-US": "Each stop needs a list pick, or typed location with a zone.", "zh-CN": "每个停点需从清单选择，或自行输入并补选区域。" })}
            </p>
          )}
        </div>
        {timeField}
      </section>
    );
  }

  /* 包車 */
  return (
    <section className="space-y-5">
      <h2 className="font-display text-lg font-medium tracking-tight">
        {t({ "zh-TW": "包車行程", "en-US": "Charter trip", "zh-CN": "包车行程" })}
      </h2>

      <div className="space-y-1.5">
        <Label className="text-[12px]">{t({ "zh-TW": "服務區域", "en-US": "Region", "zh-CN": "服务区域" })}</Label>
        <div className="flex gap-2">
          {REGIONS.map((r) => {
            const active = s.charterRegion === r;
            return (
              <button
                key={r}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  if (s.charterRegion !== r) {
                    set("charterRegion", r);
                    set("charterDurationHours", null);
                    set("vehicleModelId", null);
                  }
                }}
                className={`rounded-md border px-4 py-2 text-[13px] font-medium transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:border-border-strong hover:text-foreground"
                }`}
              >
                {t(REGION_LABEL[r])}
              </button>
            );
          })}
        </div>
      </div>

      {(["pickup", "dropoff"] as const).map((which) => {
        const value = which === "pickup" ? s.charterPickupLocation : s.charterDropoffLocation;
        const key = which === "pickup" ? "charterPickupLocation" : "charterDropoffLocation";
        return (
          <div key={which} className="space-y-1.5">
            <Label className="text-[12px]">
              {which === "pickup"
                ? t({ "zh-TW": "上車點", "en-US": "Pickup location", "zh-CN": "上车点" })
                : t({ "zh-TW": "下車點", "en-US": "Drop-off location", "zh-CN": "下车点" })}{" "}
              *
            </Label>
            <div className="flex gap-2">
              <Input
                value={value}
                onChange={(e) => set(key, e.target.value)}
                placeholder={t({ "zh-TW": "輸入地址或地點名稱", "en-US": "Type an address or place", "zh-CN": "输入地址或地点名称" })}
              />
              <Button type="button" variant="outline" onClick={() => setCharterPickerFor(which)}>
                <ListPlus className="h-3.5 w-3.5" />
                {t({ "zh-TW": "從清單選", "en-US": "From list", "zh-CN": "从清单选" })}
              </Button>
            </div>
            {triedNext && value.trim() === "" && (
              <p className="text-[12px] text-destructive">
                {t({ "zh-TW": "此欄位必填。", "en-US": "This field is required.", "zh-CN": "此栏位必填。" })}
              </p>
            )}
          </div>
        );
      })}

      <div className="space-y-1.5">
        <Label className="text-[12px]">{t({ "zh-TW": "預計時數", "en-US": "Duration", "zh-CN": "预计时数" })} *</Label>
        {charterDurations.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-4 text-center text-[13px] text-muted-foreground">
            {t({ "zh-TW": "此區域尚無包車方案，請聯繫平台。", "en-US": "No charter plans for this region yet — please contact the platform.", "zh-CN": "此区域尚无包车方案，请联系平台。" })}
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {charterDurations.map((d) => {
              const active = s.charterDurationHours === d.hours;
              return (
                <button
                  key={d.hours}
                  type="button"
                  aria-pressed={active}
                  onClick={() => {
                    set("charterDurationHours", d.hours);
                    set("vehicleModelId", null); // 時數變更 → 可選車款集合改變，重選
                  }}
                  className={`rounded-md border px-3.5 py-3 text-left transition-all hover:-translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                    active ? "border-primary bg-primary-soft/40" : "border-border bg-card hover:border-border-strong"
                  }`}
                >
                  <span className="block font-display text-[15px] font-medium">
                    {d.hours} {t({ "zh-TW": "小時", "en-US": "hours", "zh-CN": "小时" })}
                  </span>
                  <span className="numeric block text-[12px] text-muted-foreground">
                    {d.lo === d.hi
                      ? fmtMoney(d.currency, d.lo)
                      : `${fmtMoney(d.currency, d.lo)} – ${fmtMoney(d.currency, d.hi)}`}
                    <span className="ml-1">
                      {t({ "zh-TW": "依車款", "en-US": "by vehicle", "zh-CN": "依车款" })}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {triedNext && s.charterDurationHours === null && (
          <p className="text-[12px] text-destructive">
            {t({ "zh-TW": "請選擇包車時數。", "en-US": "Please choose a duration.", "zh-CN": "请选择包车时数。" })}
          </p>
        )}
      </div>

      {timeField}

      <PoiPickerDialog
        open={charterPickerFor !== null}
        onOpenChange={(o) => setCharterPickerFor(o ? charterPickerFor : null)}
        pois={opts.pois}
        region={s.charterRegion}
        onSelect={(p) => {
          if (charterPickerFor === "pickup") set("charterPickupLocation", p.name);
          if (charterPickerFor === "dropoff") set("charterDropoffLocation", p.name);
        }}
      />
    </section>
  );
}

/* ── 確認頁小元件 ───────────────────────────────────────────── */

/** D7：即時通訊單欄（品牌 icon ＋ 輸入框） */
function ContactInput({
  icon,
  label,
  value,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-border-strong">
      <span className="shrink-0">{icon}</span>
      <input
        className="min-w-0 flex-1 bg-transparent py-1.5 text-[13px] outline-none placeholder:text-muted-foreground/60"
        placeholder={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SummaryRow({ label, children }: { label: Loc; children: React.ReactNode }) {
  const { t } = useLanguage();
  return (
    <div className="grid grid-cols-[88px_1fr] gap-2">
      <span className="text-muted-foreground">{t(label)}</span>
      <span className="min-w-0 font-medium">{children}</span>
    </div>
  );
}

function TripSummary({ s }: { s: WizardState }) {
  const { t } = useLanguage();
  if (s.serviceType === "charter") {
    return (
      <span>
        {t(REGION_LABEL[s.charterRegion])} · {s.charterPickupLocation.trim()} → {s.charterDropoffLocation.trim()} ·{" "}
        {s.charterDurationHours} {t({ "zh-TW": "小時", "en-US": "hours", "zh-CN": "小时" })}
      </span>
    );
  }
  const stopsText = (
    <>
      {s.stops.map((st, i) => (
        <span key={i}>
          {i > 0 && " → "}
          {st.location.trim()}
          <Badge variant="outline" className="ml-1 align-middle text-[12px] normal-case tracking-normal">
            {t(zoneLabel(st.zone))}
          </Badge>
        </span>
      ))}
    </>
  );
  if (s.serviceType === "airport_pickup" && s.airportCode !== "") {
    return (
      <span>
        {airportName(s.airportCode, t)} → {stopsText}
      </span>
    );
  }
  if (s.serviceType === "airport_dropoff" && s.airportCode !== "") {
    return (
      <span>
        {stopsText} → {airportName(s.airportCode, t)}
      </span>
    );
  }
  return <span>{stopsText}</span>;
}
